'use client'

/**
 * HeaderCalendar — the global "market events" calendar in the top nav. The icon links to the full
 * /calendar page; hovering (desktop) opens a popover of TODAY's events merged across the economic,
 * earnings and dividend calendars. Fetches /api/market/calendar-today lazily on first hover (5-min
 * cached). A small dot on the icon signals there are scheduled events today.
 */
import { useCallback, useState } from 'react'
import Link from 'next/link'
import { CalendarDays } from 'lucide-react'

type TodayMarket = {
  date: string
  econ: { timeEt: string; event: string; released: boolean }[]
  earnings: { symbol: string; name: string; when: 'bmo' | 'amc' | 'tbd' }[]
  earningsCount: number
  dividends: { symbol: string; name: string; amount: number; yieldPct: number | null }[]
  dividendsCount: number
  total: number
}

const prettyDate = (ymd: string) => { try { return new Date(`${ymd}T12:00:00Z`).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', timeZone: 'UTC' }) } catch { return ymd } }

export default function HeaderCalendar() {
  const [data, setData] = useState<TodayMarket | null>(null)
  const [open, setOpen] = useState(false)

  const load = useCallback(() => {
    if (data) return
    fetch('/api/market/calendar-today', { cache: 'no-store' })
      .then((r) => (r.ok ? r.json() : null)).then((j) => { if (j) setData(j) }).catch(() => {})
  }, [data])

  return (
    <div className="relative" onMouseEnter={() => { setOpen(true); load() }} onMouseLeave={() => setOpen(false)}>
      <Link
        href="/calendar"
        prefetch={false}
        aria-label="Market calendar"
        title="Market calendar: economic, earnings & dividends"
        className="relative flex items-center justify-center rounded-lg p-2 text-muted-foreground transition-colors hover:bg-accent/50 hover:text-foreground"
        onFocus={load}
      >
        <CalendarDays className="h-5 w-5" />
        {data && data.total > 0 && <span className="absolute right-1.5 top-1.5 h-1.5 w-1.5 rounded-full bg-amber-400" />}
      </Link>

      {open && (
        <div className="absolute right-0 top-full z-50 mt-1 w-80 overflow-hidden rounded-xl border border-border bg-card shadow-2xl">
          <div className="border-b border-border px-3 py-2">
            <div className="text-[11px] font-bold uppercase tracking-wider text-amber-300">Today&rsquo;s market events</div>
            {data && <div className="text-[11px] text-muted-foreground">{prettyDate(data.date)}</div>}
          </div>

          <div className="max-h-[70vh] overflow-y-auto p-2 text-[13px]">
            {!data ? (
              <div className="px-2 py-4 text-center text-[12px] text-muted-foreground">Loading…</div>
            ) : data.total === 0 ? (
              <div className="px-2 py-4 text-center text-[12px] text-muted-foreground">No scheduled events today.</div>
            ) : (
              <>
                {data.econ.length > 0 && (
                  <Section title="Economic">
                    {data.econ.slice(0, 6).map((e, i) => (
                      <div key={i} className="flex items-center gap-2 rounded-md px-2 py-1 hover:bg-accent/50">
                        <span className="w-14 shrink-0 text-[11px] tabular-nums text-muted-foreground">{e.timeEt}</span>
                        <span className="min-w-0 flex-1 truncate text-foreground">{e.event}</span>
                        {e.released && <span className="shrink-0 rounded bg-muted px-1.5 py-0.5 text-[9px] font-semibold uppercase text-muted-foreground">out</span>}
                      </div>
                    ))}
                  </Section>
                )}
                {data.earningsCount > 0 && (
                  <Section title={`Earnings (${data.earningsCount})`}>
                    <div className="flex flex-wrap gap-1 px-2 py-1">
                      {data.earnings.map((r) => (
                        <Link key={r.symbol} href={`/stocks/${encodeURIComponent(r.symbol)}`} title={`${r.name} reports ${r.when === 'bmo' ? 'before open' : r.when === 'amc' ? 'after close' : 'today'}`} className="rounded bg-sky-500/10 px-1.5 py-0.5 text-[11px] font-bold text-sky-300 hover:bg-sky-500/20">{r.symbol}</Link>
                      ))}
                      {data.earningsCount > data.earnings.length && <span className="px-1 py-0.5 text-[11px] text-muted-foreground">+{data.earningsCount - data.earnings.length} more</span>}
                    </div>
                  </Section>
                )}
                {data.dividendsCount > 0 && (
                  <Section title={`Ex-dividend (${data.dividendsCount})`}>
                    <div className="flex flex-wrap gap-1 px-2 py-1">
                      {data.dividends.map((d) => (
                        <Link key={d.symbol} href={`/stocks/${encodeURIComponent(d.symbol)}`} title={`${d.name}: $${d.amount.toFixed(2)}/sh${d.yieldPct ? `, ${d.yieldPct.toFixed(1)}% yield` : ''}`} className="rounded bg-emerald-500/10 px-1.5 py-0.5 text-[11px] font-bold text-emerald-300 hover:bg-emerald-500/20">{d.symbol}</Link>
                      ))}
                      {data.dividendsCount > data.dividends.length && <span className="px-1 py-0.5 text-[11px] text-muted-foreground">+{data.dividendsCount - data.dividends.length} more</span>}
                    </div>
                  </Section>
                )}
              </>
            )}
          </div>

          <Link href="/calendar" className="block border-t border-border px-3 py-2 text-center text-[12px] font-semibold text-amber-400 transition-colors hover:bg-accent/50 hover:text-amber-300">Open full calendar →</Link>
        </div>
      )}
    </div>
  )
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="mb-1">
      <div className="px-2 py-1 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">{title}</div>
      {children}
    </div>
  )
}
